import { env } from "../config/globals";

export type PickedAddress = {
    street: string;
    city: string;
    state: string;
    zip: string;
    county: string;
    lat: number;
    lon: number;
    formatted: string;
};

const US_STATE_NAMES: Record<string, string> = {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming",
    DC: "District of Columbia",
};

/** ISO 3166-2:IN subdivision codes → full names */
const IN_STATE_NAMES: Record<string, string> = {
    AN: "Andaman and Nicobar Islands",
    AP: "Andhra Pradesh",
    AR: "Arunachal Pradesh",
    AS: "Assam",
    BR: "Bihar",
    CH: "Chandigarh",
    CT: "Chhattisgarh",
    CG: "Chhattisgarh",
    DH: "Dadra and Nagar Haveli and Daman and Diu",
    DN: "Dadra and Nagar Haveli and Daman and Diu",
    DD: "Dadra and Nagar Haveli and Daman and Diu",
    DL: "Delhi",
    GA: "Goa",
    GJ: "Gujarat",
    HR: "Haryana",
    HP: "Himachal Pradesh",
    JK: "Jammu and Kashmir",
    JH: "Jharkhand",
    KA: "Karnataka",
    KL: "Kerala",
    LA: "Ladakh",
    LD: "Lakshadweep",
    MP: "Madhya Pradesh",
    MH: "Maharashtra",
    MN: "Manipur",
    ML: "Meghalaya",
    MZ: "Mizoram",
    NL: "Nagaland",
    OR: "Odisha",
    OD: "Odisha",
    PY: "Puducherry",
    PB: "Punjab",
    RJ: "Rajasthan",
    SK: "Sikkim",
    TN: "Tamil Nadu",
    TG: "Telangana",
    TS: "Telangana",
    TR: "Tripura",
    UP: "Uttar Pradesh",
    UT: "Uttarakhand",
    UK: "Uttarakhand",
    WB: "West Bengal",
};

const CA_PROVINCE_NAMES: Record<string, string> = {
    AB: "Alberta",
    BC: "British Columbia",
    MB: "Manitoba",
    NB: "New Brunswick",
    NL: "Newfoundland and Labrador",
    NS: "Nova Scotia",
    NT: "Northwest Territories",
    NU: "Nunavut",
    ON: "Ontario",
    PE: "Prince Edward Island",
    QC: "Quebec",
    SK: "Saskatchewan",
    YT: "Yukon",
};

function normalizeStateCode(value: string): string {
    return value
        .trim()
        .toUpperCase()
        .replace(/^(US|IN|CA)-/, "");
}

/** Prefer full state/province name; expand short codes when needed. */
function toFullStateName(
    stateName: string | undefined,
    stateCode: string | undefined,
    countryCode?: string | undefined,
): string {
    const name = (stateName || "").trim();
    if (name.length > 2) {
        return name;
    }

    const rawCode = (stateCode || name || "").trim();
    if (!rawCode) {
        return "";
    }

    const code = normalizeStateCode(rawCode);
    const country = (countryCode || "").trim().toUpperCase().replace(/^USA$/, "US");

    if (country === "IN") {
        return IN_STATE_NAMES[code] || name || rawCode;
    }
    if (country === "CA") {
        return CA_PROVINCE_NAMES[code] || name || rawCode;
    }
    if (country === "US" || !country) {
        return (
            US_STATE_NAMES[code] ||
            IN_STATE_NAMES[code] ||
            CA_PROVINCE_NAMES[code] ||
            name ||
            rawCode
        );
    }

    return (
        IN_STATE_NAMES[code] ||
        US_STATE_NAMES[code] ||
        CA_PROVINCE_NAMES[code] ||
        name ||
        rawCode
    );
}

function fromGeoapify(props: Record<string, any>, lat: number, lon: number): PickedAddress {
    const house = props.housenumber || "";
    const streetName = props.street || props.name || "";
    const street = [house, streetName].filter(Boolean).join(" ").trim();
    const city = props.city || props.town || props.village || props.municipality || "";
    const state = toFullStateName(
        props.state,
        props.state_code,
        props.country_code || props.country,
    );
    const zip = props.postcode || "";
    const county = props.county || "";

    return {
        street,
        city,
        state,
        zip,
        county,
        lat,
        lon,
        formatted:
            [street, city, state, zip].filter(Boolean).join(", ") ||
            props.formatted ||
            "",
    };
}

/** Reverse-geocode lat/lon via Geoapify (preferred) or public fallback. */
export async function reverseGeocode(lat: number, lon: number): Promise<PickedAddress> {
    const apiKey = env.GEOAPIFY_API_KEY;

    if (apiKey) {
        const url =
            `https://api.geoapify.com/v1/geocode/reverse` +
            `?lat=${encodeURIComponent(lat)}` +
            `&lon=${encodeURIComponent(lon)}` +
            `&format=json` +
            `&limit=1` +
            `&apiKey=${encodeURIComponent(apiKey)}`;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error("Failed to look up address from map location");
        }
        const json = await response.json();
        const result = json?.results?.[0] || json?.features?.[0]?.properties;
        if (!result) {
            throw new Error("No address found for that location");
        }
        return fromGeoapify(result, lat, lon);
    }

    // Browser-friendly fallback (no API key). Prefer Geoapify in production.
    const fallbackUrl =
        `https://api.bigdatacloud.net/data/reverse-geocode-client` +
        `?latitude=${encodeURIComponent(lat)}` +
        `&longitude=${encodeURIComponent(lon)}` +
        `&localityLanguage=en`;

    const response = await fetch(fallbackUrl);
    if (!response.ok) {
        throw new Error("Failed to look up address from map location");
    }
    const json = await response.json();
    const street = [json.streetNumber, json.streetName].filter(Boolean).join(" ").trim();
    const city = json.city || json.locality || "";
    const state = toFullStateName(
        json.principalSubdivision,
        json.principalSubdivisionCode,
        json.countryCode,
    );
    const zip = json.postcode || "";
    const county = json.localityInfo?.administrative?.find?.(
        (item: any) => /county/i.test(item.name || "") || /county/i.test(item.description || ""),
    )?.name || "";

    if (!street && !city && !zip) {
        throw new Error("No address found for that location");
    }

    return {
        street,
        city,
        state,
        zip,
        county,
        lat,
        lon,
        formatted: [street, city, state, zip].filter(Boolean).join(", "),
    };
}

export const DEFAULT_MAP_CENTER = {
    lat: 39.7392,
    lon: -104.9903,
    zoom: 12,
};

export type GeoPosition = {
    lat: number;
    lon: number;
};

/** Read the browser's current GPS position (requires user permission). */
export function getCurrentPosition(options?: PositionOptions): Promise<GeoPosition> {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error("Location is not supported in this browser"));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    lat: position.coords.latitude,
                    lon: position.coords.longitude,
                });
            },
            (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                    reject(new Error("Location permission was denied"));
                    return;
                }
                if (error.code === error.TIMEOUT) {
                    reject(new Error("Timed out while getting your location"));
                    return;
                }
                reject(new Error("Could not get your current location"));
            },
            {
                enableHighAccuracy: true,
                timeout: 12000,
                maximumAge: 60_000,
                ...options,
            },
        );
    });
}
